/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { httpClient } from "./httpClient";
import retry from "./Retry";
import { deepCopy } from "../../general/utils";
const EXCLUDED_DETAIL_CACHE_OBJECTS = [
    "component",
    "zone",
    "board",
    "view",
    "component_zone",
    "zone_view",
    "view_board",
    'prefect/flow_run',
];
const detailCache = {};
const detailPromises = {};
const configPromises = {};
const entityMetaPromises = {};
export default class TsDataSource {
    client;
    baseUrl;
    baseUrlKey;
    constructor({ baseUrl, client } = {}) {
        this.client = client ?? httpClient;
        this.baseUrl = baseUrl;
        this.baseUrlKey = this.baseUrl || "default";
    }
    relationshipHandler = {
        get: async (target, key) => {
            const targetValue = target?.[key];
            if (targetValue === null)
                return null;
            if (targetValue !== undefined)
                return new Proxy(targetValue.data, this.dataObjectHandler);
            return await this.getToOneRelation({
                objectType: target.__sourceType,
                id: target.__sourceId,
                relation: key,
            });
        },
    };
    dataObjectHandler = {
        get: (target, key) => {
            if (key === "objectType")
                return target.type;
            if (key === "id")
                return target.id;
            if (key === "relationships") {
                const relationshipsTarget = {
                    ...(target?.relationships ?? {}),
                    __sourceType: target.type,
                    __sourceId: target.id,
                };
                return new Proxy(relationshipsTarget, this.relationshipHandler);
            }
            return target.attributes?.[key];
        },
    };
    initializeDetailCacheAndPromises(objectType) {
        detailCache[this.baseUrlKey] = detailCache[this.baseUrlKey] ?? {};
        detailCache[this.baseUrlKey][objectType] =
            detailCache[this.baseUrlKey][objectType] ?? {};
        detailPromises[this.baseUrlKey] = detailPromises[this.baseUrlKey] ?? {};
        detailPromises[this.baseUrlKey][objectType] =
            detailPromises[this.baseUrlKey][objectType] ?? Promise.resolve();
    }
    updateDetailCache(response, objectType) {
        if (objectType in EXCLUDED_DETAIL_CACHE_OBJECTS)
            return;
        detailCache[this.baseUrlKey] = detailCache[this.baseUrlKey] ?? {};
        detailCache[this.baseUrlKey][objectType] =
            detailCache[this.baseUrlKey][objectType] || {};
        return response.data.data.map((object) => {
            detailCache[this.baseUrlKey][objectType][object.id] =
                detailCache[this.baseUrlKey][objectType][object.id] || object;
            return new Proxy(detailCache[this.baseUrlKey][objectType][object.id], this.dataObjectHandler);
        });
    }
    getLocalStorageKey(o) {
        return `${o}-${this.baseUrlKey}`;
    }
    getSavedConfig(key) {
        const savedConfig = JSON.parse(localStorage.getItem(key) || "null");
        if (savedConfig === null)
            return null;
        const expiry = new Date(savedConfig["expiry"]);
        return { data: savedConfig.data, expiry };
    }
    isConfigExpired(expiry) {
        const now = new Date();
        return !(expiry && now < expiry);
    }
    fetchAndSaveConfig(endpoint, key) {
        const anHourFromNow = new Date();
        anHourFromNow.setHours(anHourFromNow.getHours() + 1);
        if (!configPromises[key]) {
            configPromises[key] = this.client()
                .get(endpoint, { baseURL: this.baseUrl })
                .then((config) => {
                const savedConfig = {
                    expiry: anHourFromNow,
                    data: config.data,
                };
                localStorage.setItem(key, JSON.stringify(savedConfig));
                return savedConfig.data;
            })
                .finally(() => {
                delete configPromises[key];
            });
        }
        return configPromises[key];
    }
    async custom(endpoint, method, body) {
        switch (method) {
            case "POST":
                return await this.client().post(endpoint, {
                    baseUrl: this.baseUrl,
                    data: body,
                });
            case "DELETE":
                return await this.client().delete(endpoint, {
                    baseUrl: this.baseUrl,
                });
        }
    }
    getConfig(endpoint) {
        const key = this.getLocalStorageKey(endpoint);
        const savedConfig = this.getSavedConfig(key);
        if (savedConfig && !this.isConfigExpired(savedConfig.expiry)) {
            return Promise.resolve(savedConfig.data);
        }
        else {
            return this.fetchAndSaveConfig(endpoint, key);
        }
    }
    async attributeMetadata() {
        return this.getConfig("/_config/attribute_metadata");
    }
    async relationshipConfig() {
        return this.getConfig("/_config/relationships");
    }
    addIds(attributes) {
        for (const objectType of Object.values(attributes)) {
            if (!objectType.hasOwnProperty("uid")) {
                objectType["id"] = {
                    authoritative: null,
                    available_on_relationships: null,
                    cardinality: 99999,
                    description: null,
                    display_name: null,
                    python_type: "str",
                    source: null,
                };
            }
        }
    }
    flattenAttributes(attributes, relationships) {
        this.addIds(attributes);
        const newAttributes = deepCopy(attributes);
        for (const entity in relationships) {
            // just deal with one-side relationships
            const oneRelationships = relationships[entity]?.one;
            if (oneRelationships) {
                for (const [relationship, objType] of Object.entries(oneRelationships)) {
                    newAttributes[entity][`${relationship}.id`] = {
                        available_on_relationships: true,
                        python_type: "str",
                    };
                    for (const [key, meta] of Object.entries(attributes[objType])) {
                        if (meta.available_on_relationships) {
                            newAttributes[entity][`${relationship}.${key}`] = meta;
                        }
                    }
                }
            }
        }
        return newAttributes;
    }
    async getEntityMeta() {
        if (!entityMetaPromises[this.baseUrlKey]) {
            entityMetaPromises[this.baseUrlKey] = (async () => {
                const key = this.getLocalStorageKey("entityMeta");
                let savedEntityMeta = JSON.parse(localStorage.getItem(key) || "null");
                const expiry = savedEntityMeta === null ? null : new Date(savedEntityMeta["expiry"]);
                // setting now and an hour from now
                const now = new Date();
                const anHourFromNow = new Date(now);
                anHourFromNow.setHours(now.getHours() + 1);
                // check if entityMeta exists and is not expired
                if (expiry === null || now > expiry) {
                    const attributes = await this.attributeMetadata();
                    const relationships = await this.relationshipConfig();
                    savedEntityMeta = {
                        expiry: anHourFromNow,
                        data: {
                            flatAttributes: this.flattenAttributes(attributes, relationships),
                            relationships,
                        },
                    };
                    localStorage.setItem(key, JSON.stringify(savedEntityMeta));
                }
                return savedEntityMeta.data;
            })().finally(() => {
                delete entityMetaPromises[this.baseUrlKey];
            });
        }
        return entityMetaPromises[this.baseUrlKey];
    }
    async getOne({ objectType, id }) {
        this.initializeDetailCacheAndPromises(objectType);
        if (id in detailCache[this.baseUrlKey][objectType]) {
            // if object is in cache, return it
            return new Proxy(detailCache[this.baseUrlKey][objectType][id], this.dataObjectHandler);
        }
        if (!(id in detailPromises[this.baseUrlKey][objectType])) {
            // if promise is not in progress, start a new one
            detailPromises[this.baseUrlKey][objectType][id] = this.client()
                .get(`/${objectType}/${id}`, { baseURL: this.baseUrl })
                .then((response) => {
                if (!EXCLUDED_DETAIL_CACHE_OBJECTS.includes(objectType)) {
                    detailCache[this.baseUrlKey][objectType][id] = response.data.data;
                }
                return new Proxy(response.data.data, this.dataObjectHandler);
            })
                .catch((error) => {
                if (error.response.status === 404)
                    return null;
                throw error;
            })
                .finally(() => {
                delete detailPromises[this.baseUrlKey][objectType][id]; // remove promise from in-progress list when it's done
            });
        }
        return detailPromises[this.baseUrlKey][objectType][id]; // return existing promise if it's in progress
    }
    async getToOneRelation({ objectType, id, relation, }) {
        return await this.client()
            .get(`/${objectType}:to-one/${id}/${relation}`, { baseURL: this.baseUrl })
            .then((response) => {
            return new Proxy(response.data.data, this.dataObjectHandler);
        })
            .catch((error) => {
            if (error.response.status === 404)
                return null;
            throw error;
        });
    }
    async getByIds({ objectType, ids, }) {
        this.initializeDetailCacheAndPromises(objectType);
        const promiseBulk = ids.map((id) => this.getOne({ objectType, id }));
        return await Promise.all(promiseBulk);
    }
    async getListPage({ objectType, page, pageSize, filter, sortBy, }) {
        this.initializeDetailCacheAndPromises(objectType);
        return await this.client()
            .get(`/${objectType}`, {
            baseURL: this.baseUrl,
            params: {
                page: page,
                page_size: pageSize,
                filter: filter,
                sort_by: sortBy,
            },
        })
            .then((response) => {
            return this.updateDetailCache(response, objectType);
        })
            .catch((error) => {
            if (error.response.status === 404)
                return null;
            throw error;
        });
    }
    async deleteByID({ objectType, id }) {
        this.initializeDetailCacheAndPromises(objectType);
        return await this.client()
            .delete(`/${objectType}/${id}`, { baseURL: this.baseUrl })
            .then(() => {
            if (id in detailCache[this.baseUrlKey][objectType]) {
                delete detailCache[this.baseUrlKey][objectType][id];
            }
        })
            .catch((error) => {
            if (error.response.status === 404)
                return null;
            throw error;
        });
    }
    async upsert({ payload, objectType }) {
        this.initializeDetailCacheAndPromises(objectType);
        return await this.client()
            .post(`/${objectType}:upsert`, { data: payload }, { baseURL: this.baseUrl })
            .then((response) => {
            return this.updateDetailCache(response, objectType);
        })
            .catch((error) => {
            if (error.response.status === 404)
                return null;
            throw error;
        });
    }
}
__decorate([
    retry(3),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", Promise)
], TsDataSource.prototype, "getConfig", null);
